import { useEffect, useState } from 'react';
import { csvDocument, D, DOCUMENT_TYPE_META, fiscalDate, formatMoney, PAYMENT_METHODS, type DocumentType, type PaymentMethod } from '@makila/fiscal';
import { Download } from 'lucide-react';
import { mzDateTime } from '../lib/receipt';
import { api } from './api';
import { Alert, Button, Card, Field, Input } from './ui';
import { useLoad } from './useLoad';

interface Period { from: string; to: string; training: boolean }

/** Período (datas de Maputo) e documentos de formação ou reais. */
export function usePeriod(): [Period, (p: Period) => void] {
  const today = fiscalDate();
  const [p, setP] = useState<Period>({ from: today, to: today, training: false });
  const me = useLoad<{ tenant: { training_mode: boolean } }>('/auth/me');
  // Enquanto a empresa está em formação, mostra por omissão os documentos de formação.
  useEffect(() => { if (me.data) setP((x) => ({ ...x, training: me.data!.tenant.training_mode })); }, [me.data]);
  return [p, setP];
}

const qs = (p: Period) => `from=${p.from}&to=${p.to}&training=${p.training ? 1 : 0}`;

export function PeriodBar({ p, set, children }: { p: Period; set: (p: Period) => void; children?: React.ReactNode }) {
  const today = fiscalDate();
  const shift = (days: number) => { const d = new Date(`${today}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + days); return d.toISOString().slice(0, 10); };
  return (
    <div className="mb-4 flex flex-wrap items-end gap-3">
      <Field label="De"><Input type="date" value={p.from} max={p.to} onChange={(e) => set({ ...p, from: e.target.value })} /></Field>
      <Field label="Até"><Input type="date" value={p.to} min={p.from} onChange={(e) => set({ ...p, to: e.target.value })} /></Field>
      <div className="flex gap-2">
        <Button variant="ghost" onClick={() => set({ ...p, from: today, to: today })}>Hoje</Button>
        <Button variant="ghost" onClick={() => set({ ...p, from: shift(-6), to: today })}>7 dias</Button>
        <Button variant="ghost" onClick={() => set({ ...p, from: `${today.slice(0, 8)}01`, to: today })}>Este mês</Button>
      </div>
      <label className="flex h-11 items-center gap-2 text-sm">
        <input type="checkbox" className="h-5 w-5" checked={p.training} onChange={(e) => set({ ...p, training: e.target.checked })} />
        Documentos de formação
      </label>
      <div className="ml-auto">{children}</div>
    </div>
  );
}

/** Descarrega um CSV que abre no Excel em português (; e BOM — @makila/fiscal csvDocument). */
export function downloadCsv(name: string, rows: (string | number)[][]) {
  const url = URL.createObjectURL(new Blob([csvDocument(rows)], { type: 'text/csv;charset=utf-8' }));
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
const num = (v: string | number) => String(v).replace('.', ',');

interface Sale { id: string; doc_type: DocumentType; origin_full_number?: string | null; full_number: string; issue_date: string; system_entry: string; gross_total: string; tax_total: string; customer_name: string | null; training: boolean; integrity_ok: boolean; terminal_name: string | null; operator_name: string | null }

export function SalesPage() {
  const [p, setP] = usePeriod();
  const { data, error } = useLoad<{ items: Sale[]; totals: { gross: string; tax: string; count: number }; truncated: boolean }>(`/sales?${qs(p)}`);
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div className="max-w-6xl">
      <PeriodBar p={p} set={setP}>
        {data && <Button variant="ghost" onClick={() => downloadCsv(`vendas-${p.from}-${p.to}.csv`, [
          ['Documento', 'Data', 'Hora', 'Caixa', 'Operador', 'Cliente', 'IVA', 'Total'],
          ...data.items.map((s) => [s.full_number, s.issue_date, s.system_entry.slice(11), s.terminal_name ?? '', s.operator_name ?? '', s.customer_name ?? 'Consumidor Final', num(s.tax_total), num(s.gross_total)]),
        ])}><Download size={16} /> Excel</Button>}
      </PeriodBar>
      {error && <Alert>{error}</Alert>}
      {data && (
        <Card title={`${data.totals.count} documentos · ${formatMoney(data.totals.gross)} MT (IVA ${formatMoney(data.totals.tax)})`}>
          {data.truncated && <div className="mb-3"><Alert tone="info">Mostram-se os 1000 mais recentes; escolha um período mais curto ou exporte.</Alert></div>}
          {!data.items.length && <p className="text-muted">Sem vendas neste período. As vendas chegam das caixas quando há internet.</p>}
          <ul className="divide-y divide-[var(--border)]">
            {data.items.map((s) => (
              <li key={s.id}>
                <button onClick={() => setOpen(open === s.id ? null : s.id)} className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 py-3 text-left hover:bg-surface-2">
                  <span className="w-48 font-mono text-sm">{s.full_number}</span>
                  <span className="text-sm text-muted">{s.issue_date.split('-').reverse().join('/')} {s.system_entry.slice(11, 16)}</span>
                  <span className="text-sm">{s.terminal_name} · {s.operator_name ?? '—'}{s.customer_name ? ` · ${s.customer_name}` : ''}</span>
                  {s.origin_full_number && <span className="text-xs text-muted">ref. {s.origin_full_number}</span>}
                  {!s.integrity_ok && <span className="rounded bg-amber-100 px-2 text-xs font-semibold text-amber-900">Para rever</span>}
                  {s.training && <span className="rounded bg-surface-2 px-2 text-xs">formação</span>}
                  <span className="num ml-auto font-semibold">{s.doc_type === 'NC' ? '−' : ''}{formatMoney(s.gross_total)}</span>
                </button>
                {open === s.id && <SaleDetail id={s.id} />}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function SaleDetail({ id }: { id: string }) {
  const { data, error } = useLoad<{ document: Sale & { net_total: string; tendered: string; change: string; customer_nuit: string | null; reason: string | null }; lines: { line_no: number; description: string; quantity: string; unit_price: string; tax_rate: string; exemption_code: string | null; total_amount: string }[]; payments: { method: PaymentMethod; amount: string; reference: string | null }[] }>(`/sales/${id}`);
  if (error) return <Alert>{error}</Alert>;
  if (!data) return <p className="p-3 text-muted">A carregar…</p>;
  const d = data.document;
  return (
    <div className="mb-3 rounded-lg bg-surface-2 p-4 text-sm">
      <div className="mb-2 font-semibold">{DOCUMENT_TYPE_META[d.doc_type].label} {d.full_number} · {d.customer_name ?? 'Consumidor Final'}{d.customer_nuit ? ` · NUIT ${d.customer_nuit}` : ''}</div>
      {d.origin_full_number && <div className="mb-2">Referente a {d.origin_full_number} · Motivo: {d.reason}</div>}
      <table className="w-full">
        <tbody>
          {data.lines.map((l) => (
            <tr key={l.line_no}><td className="py-1">{l.description}</td><td className="num text-right">{num(new D(l.quantity).toString())} × {formatMoney(l.unit_price)}</td><td className="num w-24 text-right">{new D(l.tax_rate).toString()}%{l.exemption_code ? ` ${l.exemption_code}` : ''}</td><td className="num w-28 text-right">{formatMoney(l.total_amount)}</td></tr>
          ))}
        </tbody>
      </table>
      <div className="mt-2 flex flex-wrap gap-4 border-t border-base pt-2">
        <span>Base {formatMoney(d.net_total)}</span><span>IVA {formatMoney(d.tax_total)}</span><span className="font-semibold">Total {formatMoney(d.gross_total)}</span>
        {data.payments.map((p, i) => <span key={i}>{PAYMENT_METHODS[p.method]} {formatMoney(p.amount)}{p.reference ? ` (${p.reference})` : ''}</span>)}
        {new D(d.change).greaterThan(0) && <span>Troco {formatMoney(d.change)}</span>}
      </div>
    </div>
  );
}

interface Session { id: string; status: 'open' | 'closed'; opened_at: string; closed_at: string | null; z_number: number | null; opening_float: string; counted_cash: string | null; expected_cash: string | null; difference: string | null; terminal_name: string | null; store_name: string | null; opened_by: string | null; closed_by: string | null; report: { grossTotal: string; cashOut: string; cashIn: string } | null }

export function CashSessionsPage() {
  const [p, setP] = usePeriod();
  const { data, error } = useLoad<{ items: Session[] }>(`/cash-sessions?${qs(p)}`);
  return (
    <div className="max-w-6xl">
      <PeriodBar p={p} set={setP} />
      {error && <Alert>{error}</Alert>}
      {data && (
        <Card title="Fechos de caixa">
          {!data.items.length && <p className="text-muted">Sem turnos neste período.</p>}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-muted"><tr><th className="py-2">Caixa</th><th>Abertura</th><th>Fecho</th><th>Z</th><th className="text-right">Vendas</th><th className="text-right">Esperado</th><th className="text-right">Contado</th><th className="text-right">Diferença</th></tr></thead>
              <tbody className="divide-y divide-[var(--border)]">
                {data.items.map((s) => {
                  const diff = s.difference === null ? null : new D(s.difference);
                  return (
                    <tr key={s.id}>
                      <td className="py-2">{s.store_name} · {s.terminal_name}</td>
                      <td>{mzDateTime(s.opened_at)}<div className="text-xs text-muted">{s.opened_by}</div></td>
                      <td>{s.closed_at ? mzDateTime(s.closed_at) : <span className="rounded bg-green-100 px-2 text-xs font-semibold text-green-900">aberta</span>}<div className="text-xs text-muted">{s.closed_by}</div></td>
                      <td>{s.z_number ?? '—'}</td>
                      <td className="num text-right">{s.report ? formatMoney(s.report.grossTotal) : '—'}</td>
                      <td className="num text-right">{s.expected_cash ? formatMoney(s.expected_cash) : '—'}</td>
                      <td className="num text-right">{s.counted_cash ? formatMoney(s.counted_cash) : '—'}</td>
                      <td className={`num text-right font-semibold ${diff && !diff.isZero() ? 'text-brand-700' : ''}`}>{diff === null ? '—' : diff.isZero() ? 'certa' : `${diff.isNegative() ? 'falta ' : 'sobra '}${formatMoney(diff.abs())}`}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

interface Summary {
  totals: { documents: number; gross: string; net: string; tax: string; average_ticket: string; to_review: number; discounts: string };
  discountsByOperator: { operator: string; approved_by: string; discounts: string }[];
  discountsByPromotion: { promotion: string; quantity: string; discounts: string }[];
  tips: { employee: string; cash: string; other: string }[];
  byDay: { day: string; documents: number; gross: string }[];
  byMethod: { method: PaymentMethod; amount: string }[];
  byRate: { rate: string; net: string; tax: string }[];
  byProduct: { description: string; quantity: string; total: string }[];
  byOperator: { operator: string; documents: number; gross: string }[];
  byTerminal: { terminal: string; documents: number; gross: string }[];
}

export function ReportsPage() {
  const [p, setP] = usePeriod();
  const [data, setData] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { api<Summary>(`/reports/summary?${qs(p)}`).then(setData, (e) => setError((e as Error).message)); }, [p]);
  const exportAll = () => data && downloadCsv(`relatorio-${p.from}-${p.to}.csv`, [
    ['Relatório de vendas', `${p.from} a ${p.to}`], [],
    ['Total vendido', num(data.totals.gross)], ['Base tributável', num(data.totals.net)], ['IVA', num(data.totals.tax)], ['Documentos', data.totals.documents], ['Talão médio', num(data.totals.average_ticket)], ['Descontos concedidos', num(data.totals.discounts)], [],
    ['Descontos por operador', 'Autorizado por', 'Valor'], ...data.discountsByOperator.map((r) => [r.operator, r.approved_by, num(r.discounts)]), [],
    ['Gorjetas (fora das vendas)', 'Numerário', 'Outros meios'], ...data.tips.map((r) => [r.employee, num(r.cash), num(r.other)]), [],
    ['Promoção', 'Quantidade', 'Desconto'], ...data.discountsByPromotion.map((r) => [r.promotion, num(r.quantity), num(r.discounts)]), [],
    ['Por dia', 'Documentos', 'Total'], ...data.byDay.map((r) => [r.day, r.documents, num(r.gross)]), [],
    ['Por meio de pagamento', 'Valor'], ...data.byMethod.map((r) => [PAYMENT_METHODS[r.method], num(r.amount)]), [],
    ['Taxa de IVA', 'Base', 'IVA'], ...data.byRate.map((r) => [`${num(r.rate)}%`, num(r.net), num(r.tax)]), [],
    ['Artigo', 'Quantidade', 'Total'], ...data.byProduct.map((r) => [r.description, num(r.quantity), num(r.total)]), [],
    ['Operador', 'Documentos', 'Total'], ...data.byOperator.map((r) => [r.operator, r.documents, num(r.gross)]), [],
    ['Caixa', 'Documentos', 'Total'], ...data.byTerminal.map((r) => [r.terminal, r.documents, num(r.gross)]),
  ]);
  return (
    <div className="max-w-6xl">
      <PeriodBar p={p} set={setP}>{data && <Button variant="ghost" onClick={exportAll}><Download size={16} /> Excel</Button>}</PeriodBar>
      {error && <Alert>{error}</Alert>}
      {data && (
        <div className="space-y-4">
          {data.totals.to_review > 0 && <Alert>{data.totals.to_review} documento(s) neste período chegaram com problemas de integridade e estão marcados para revisão (Vendas → "Para rever").</Alert>}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat label="Total vendido" value={`${formatMoney(data.totals.gross)} MT`} />
            <Stat label="IVA" value={`${formatMoney(data.totals.tax)} MT`} />
            <Stat label="Documentos" value={String(data.totals.documents)} />
            <Stat label="Talão médio" value={`${formatMoney(data.totals.average_ticket)} MT`} />
          </div>
          {new D(data.totals.discounts).greaterThan(0) && (
            <Table title={`Descontos concedidos: ${formatMoney(data.totals.discounts)} MT`} head={['Operador', 'Autorizado por', 'Descontos']} rows={data.discountsByOperator.map((r) => [r.operator, r.approved_by, formatMoney(r.discounts)])} />
          )}
          {data.tips.length > 0 && (
            <Table title="Gorjetas (fora das vendas)" head={['Empregado', 'Numerário', 'Outros meios']} rows={data.tips.map((r) => [r.employee, formatMoney(r.cash), formatMoney(r.other)])} />
          )}
          {data.discountsByPromotion.length > 0 && (
            <Table title="Promoções" head={['Promoção', 'Qtd.', 'Desconto']} rows={data.discountsByPromotion.map((r) => [r.promotion, num(new D(r.quantity).toString()), formatMoney(r.discounts)])} />
          )}
          <div className="grid gap-4 lg:grid-cols-2">
            <Table title="Por meio de pagamento" head={['Meio', 'Valor']} rows={data.byMethod.map((r) => [PAYMENT_METHODS[r.method], formatMoney(r.amount)])} />
            <Table title="Por taxa de IVA" head={['Taxa', 'Base', 'IVA']} rows={data.byRate.map((r) => [`${num(new D(r.rate).toString())}%`, formatMoney(r.net), formatMoney(r.tax)])} />
            <Table title="Artigos mais vendidos" head={['Artigo', 'Qtd.', 'Total']} rows={data.byProduct.map((r) => [r.description, num(new D(r.quantity).toString()), formatMoney(r.total)])} />
            <Table title="Por dia" head={['Dia', 'Docs.', 'Total']} rows={data.byDay.map((r) => [r.day.split('-').reverse().join('/'), String(r.documents), formatMoney(r.gross)])} />
            <Table title="Por operador" head={['Operador', 'Docs.', 'Total']} rows={data.byOperator.map((r) => [r.operator, String(r.documents), formatMoney(r.gross)])} />
            <Table title="Por caixa" head={['Caixa', 'Docs.', 'Total']} rows={data.byTerminal.map((r) => [r.terminal, String(r.documents), formatMoney(r.gross)])} />
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return <div className="surface rounded-xl p-4"><div className="text-sm text-muted">{label}</div><div className="num mt-1 text-2xl font-bold">{value}</div></div>;
}

function Table({ title, head, rows }: { title: string; head: string[]; rows: string[][] }) {
  return (
    <Card title={title}>
      {!rows.length ? <p className="text-muted">Sem dados.</p> : (
        <table className="w-full text-sm">
          <thead className="text-left text-muted"><tr>{head.map((h, i) => <th key={h} className={`py-1 ${i ? 'text-right' : ''}`}>{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-[var(--border)]">{rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} className={`py-1.5 ${j ? 'num text-right' : ''}`}>{c}</td>)}</tr>)}</tbody>
        </table>
      )}
    </Card>
  );
}
